import { UserIdentity } from './userIdentity';
import {
  IDENTITY_STORAGE_KEY,
  PROFILE_STORAGE_KEY,
  USER_SETTINGS_STORAGE_KEY,
  AMP_ID_STORAGE_KEY,
  USER_SEGMENTS_STORAGE_KEY
} from './constants';
import { initStorage, shouldStorageChange } from '../utils/storage';

import login, { isLoggedIn } from './login';
import logout from './logout';
import requestMagicLink from './requestMagicLink';
import redeemMagicLink from './redeemMagicLink';
import { extendSession, heartbeat } from './extendSession';
import clearSession from './clearSession';
import options, { Options } from './options';
import requestOTALink from './requestOTALink';
import redeemOTALink from './redeemOTALink';

import { UserProfile } from './userProfile';
import { UserSegments } from './userSegments';
import getUserProfile from './getUserProfile';
import getUserSegments from './getUserSegments';
import updateUserProfile from './updateUserProfile';
import { uploadAvatar, deleteAvatar } from './avatar';
import signUp from './signUp';
import updatePassword from './updatePassword';
import requestResetPassword from './requestResetPassword';
import resetPassword from './resetPassword';
import requestVerifyEmail from './requestVerifyEmail';
import verifyEmail from './verifyEmail';
import { getConfig, ConfigOptions } from './getConfig';
import requestDeleteAccount, {
  approveDeleteAccount,
  declineDeleteAccount
} from './requestDeleteAccount';
import { getUserSettings, UserSettings } from './getUserSettings';
import getThirdPartyTokens from './getThirdPartyTokens';

import listGdprReports from './listGdprReports';
import getGdprReport from './getGdprReport';

import initFacebookLogin from './initFacebookLogin';
import initGoogleLogin, { initGoogleAuthSDK, initGoogleAuth } from './initGoogleLogin';
import initAppleSignOn from './initAppleSignOn';
import initiateOIDC from './initiateOIDC';
import signInWithOIDC from './signInWithOIDC';
import signInWithOIDCPKCE from './signInWithOIDCPKCE';
import loginWithArcIdentityAsOIDCProvider from './loginWithArcIdentityAsOIDCProvider';
import unlinkOIDC from './unlinkOIDC';
import facebookSignOn from './facebookSignOn';
import googleSignOn, { signOnSwgUser } from './googleSignOn';
import signInWithGoogle from './signInWithGoogle';
import appleSignOn from './appleSignOn';
import unlinkSocialIdentity from './unlinkSocialIdentity';
import { LegacyGoogleSettings } from './socialSignOnTypes';

import getSwgAccessToken from './getSwgAccessToken';
import checkSwgLogin from './checkSwgLogin';
import relinkGoogleAccount from './relinkGoogleAccount';

import CookieStorage from '../utils/cookieStorage';

/** @hidden */
declare global {
  interface Window {
    FB: any;
    gapi: typeof gapi;
  }
}

export default abstract class Identity {
  /** @hidden */
  static _version = require('../../package.json').version;

  /** @hidden */
  private static _userIdentity: UserIdentity;

  /** @hidden */
  private static _userProfile: UserProfile | null;

  /** @hidden */
  private static _userSegments: UserSegments | null;

  /** @hidden */
  private static _userSettings: UserSettings | null;

  /** @hidden */
  public static _configOptions: ConfigOptions | null;

  /** @hidden */
  public static _isSession: boolean;

  /** @hidden */
  private static _userIdentityStorage = initStorage();

  /** @hidden */
  private static _ampReaderId: string | null | undefined;

  /** @hidden */
  static _profileStorageOpt: Options['profileStorageOpt'];

  /** @hidden */
  static _selectedStorage = initStorage();

  /** @hidden */
  static apiOrigin = '';

  /** @hidden */
  static _baseDomain = '';

  /** @hidden */
  static _google: LegacyGoogleSettings | typeof google = {
    useCustomButton: false
  };

  /** @hidden */
  static _useCustomFBButton = false;

  /** @hidden */
  static _facebookClientId: string | undefined;

  /** @hidden */
  static _appleClientId: string | undefined;

  /**
   * An object which holds the user's identity information. Things like the `accessToken`, `refreshToken` and `uuid` will be stored here. e.g.
   * ```json
   * {
   *   "uuid": "c7e9fed4-bedf-428b-a694-1bbd21721cf7",
   *   "accessToken": "c7e9fed4bedf428b.a6941bbd21721cf7...",
   *   "refreshToken": "ckd783hd4bedf428b.a699n39247381721cf7...",
   *   "impersonator": "jane_csr"
   * }
   * ```
   *
   * The `userIdentity` property is a shortcut to `localStorage['ArcId.USER_INFO']` (or `sessionStorage['ArcId.USER_INFO']`) or our client-side cookie storage,
   * depending on what has been selected.
   */
  static get userIdentity(): UserIdentity {
    if (!this._userIdentity && this._userIdentityStorage) {
      this._userIdentity = JSON.parse(
        this._userIdentityStorage.getItem(IDENTITY_STORAGE_KEY) || '{}'
      );
    }
    return this._userIdentity;
  }

  static set userIdentity(v: UserIdentity) {
    if (shouldStorageChange(this._userIdentityStorage, this._isSession, IDENTITY_STORAGE_KEY)) {
      if (this._userIdentityStorage instanceof CookieStorage) {
        this._userIdentityStorage.removeItem(IDENTITY_STORAGE_KEY);
      }
      this._userIdentityStorage = initStorage(this._baseDomain, this._isSession);
    }

    if (!v.uuid && !v.accessToken && !v.refreshToken && !v.impersonator) {
      this._userIdentityStorage.removeItem(IDENTITY_STORAGE_KEY);
    }

    this._userIdentityStorage.setItem(IDENTITY_STORAGE_KEY, JSON.stringify(v));
    this._userIdentity = v;
  }

  /**
   * An object which holds the user's profile. This object holds things like `firstName`, `lastName`, `email`, `address` and any other attributes.
   * The `userProfile` property is a shortcut to `localStorage['ArcId.USER_PROFILE']`.
   *
   * When a user has registered with social sign in providers (FACEBOOK, GOOGLE), the `userName` property on the objects in the `userProfile.identities` array will represent the user ID of the account the user has with FACEBOOK or GOOGLE.
   */
  static get userProfile(): UserProfile | null {
    if (!this._userProfile) {
      this._userProfile = JSON.parse(this._selectedStorage.getItem(PROFILE_STORAGE_KEY) || null);
    }

    return this._userProfile;
  }

  static set userProfile(u: UserProfile | null) {
    if (u === null) {
      this._selectedStorage.removeItem(PROFILE_STORAGE_KEY);
    }

    if (shouldStorageChange(this._selectedStorage, this._isSession, PROFILE_STORAGE_KEY)) {
      if (this._selectedStorage instanceof CookieStorage) {
        this._selectedStorage.removeItem(PROFILE_STORAGE_KEY);
      }
      this._selectedStorage = initStorage(
        this._baseDomain,
        this._isSession,
        this._profileStorageOpt?.storageExp
      );
    }
    if (this._profileStorageOpt?.ignoreProfile) {
      this._selectedStorage.removeItem(PROFILE_STORAGE_KEY);
    } else {
      this._selectedStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(u));
    }
    this._userProfile = u;
  }

  /**
   * An object which holds the user's segments. This object holds things like `id`, `segment`, `updated`.
   * The `userSegments` property is a shortcut to `localStorage['ArcId.USER_SEGMENTS']`.
   */

  static get userSegments(): UserSegments | null {
    if (!this._userSegments) {
      this._userSegments = JSON.parse(
        this._selectedStorage.getItem(USER_SEGMENTS_STORAGE_KEY) || null
      );
    }

    return this._userSegments;
  }

  static set userSegments(us: UserSegments | null) {
    this._selectedStorage.setItem(USER_SEGMENTS_STORAGE_KEY, JSON.stringify(us));
    this._userSegments = us;
  }

  static get configOptions() {
    return this._configOptions;
  }

  /**
   * An object which holds the user's settings. This object holds user settings like third party sso keys (e.g. disqus).
   * The `userSettings` property is a shortcut to `localStorage['ArcId.USER_SETTINGS']`.
   */
  static get userSettings(): UserSettings | null {
    if (!this._userSettings && !this._userSettings) {
      this._userSettings = JSON.parse(
        this._selectedStorage.getItem(USER_SETTINGS_STORAGE_KEY) || null
      );
    }

    return this._userSettings;
  }

  static set userSettings(u: UserSettings | null) {
    if (u === null) {
      this._selectedStorage.removeItem(USER_SETTINGS_STORAGE_KEY);
    }

    this._selectedStorage.setItem(USER_SETTINGS_STORAGE_KEY, JSON.stringify(u));
  }

  /**
   * When setting the base domain, cookie storage will be used to store the user's identity using that domain,
   */
  static set baseDomain(v: string) {
    this._userIdentityStorage = initStorage(v);
    this._baseDomain = v;
  }

  /**`
   * An user's AMP READER_ID. The `ampReaderId` property is a shortcut to `localStorage['ArcId.AMP_READER_IDS'].
   */
  static get ampReaderId(): string | null | undefined {
    if (!this._ampReaderId) {
      this._ampReaderId = JSON.parse(this._selectedStorage.getItem(AMP_ID_STORAGE_KEY) || null);
    }

    return this._ampReaderId;
  }

  static set ampReaderId(u: string | null | undefined) {
    if (u === null || u === undefined) {
      this._selectedStorage.removeItem(AMP_ID_STORAGE_KEY);
    } else if (!!u) {
      this._selectedStorage.setItem(AMP_ID_STORAGE_KEY, JSON.stringify(u));
    }
  }

  /**
   * An object which holds the storage preferences for `userProfile`. This object holds `storageExp` and `ignoreProfile`.
   */
  static get profileStorageOpt(): object | undefined {
    return this._profileStorageOpt;
  }

  static set profileStorageOpt(u: object | undefined) {
    if (!!u) {
      this._profileStorageOpt = u;
    }
  }

  static options = options;
  static login = login;
  static isLoggedIn = isLoggedIn;
  static extendSession = extendSession;
  static clearSession = clearSession;
  static heartbeat = heartbeat;
  static logout = logout;
  static getUserSettings = getUserSettings;
  static getUserProfile = getUserProfile;
  static getUserSegments = getUserSegments;
  static updateUserProfile = updateUserProfile;
  static uploadAvatar = uploadAvatar;
  static deleteAvatar = deleteAvatar;
  static signUp = signUp;
  static updatePassword = updatePassword;
  static requestResetPassword = requestResetPassword;
  static resetPassword = resetPassword;
  static requestDeleteAccount = requestDeleteAccount;
  static approveDeleteAccount = approveDeleteAccount;
  static declineDeleteAccount = declineDeleteAccount;
  static getConfig = getConfig;
  static initFacebookLogin = initFacebookLogin;
  static facebookSignOn = facebookSignOn;
  static initGoogleLogin = initGoogleLogin;
  static initGoogleAuthSDK = initGoogleAuthSDK;
  static initGoogleAuth = initGoogleAuth;
  static initiateOIDC = initiateOIDC;
  static signInWithOIDC = signInWithOIDC;
  static signInWithOIDCPKCE = signInWithOIDCPKCE;
  static loginWithArcIdentityAsOIDCProvider = loginWithArcIdentityAsOIDCProvider;
  static unlinkOIDC = unlinkOIDC;
  static googleSignOn = googleSignOn;
  static signInWithGoogle = signInWithGoogle;
  static initAppleSignOn = initAppleSignOn;
  static appleSignOn = appleSignOn;
  static requestMagicLink = requestMagicLink;
  static redeemMagicLink = redeemMagicLink;
  static requestVerifyEmail = requestVerifyEmail;
  static verifyEmail = verifyEmail;
  static listGdprReports = listGdprReports;
  static getGdprReport = getGdprReport;
  static getThirdPartyTokens = getThirdPartyTokens;
  static unlinkSocialIdentity = unlinkSocialIdentity;
  static requestOTALink = requestOTALink;
  static redeemOTALink = redeemOTALink;

  static getSwgAccessToken = getSwgAccessToken;
  static checkSwgLogin = checkSwgLogin;
  static relinkGoogleAccount = relinkGoogleAccount;
  static signOnSwgUser = signOnSwgUser;
}
